import { TableCell, TableRow, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Holding } from '../../domain/holdings';
import { date, money, units as formatUnits } from '../../lib/format';
import { HOLDER_TYPE_LABELS } from '../../lib/labels';
import { sumBy, useTenantScope } from '../../store/selectors';
import DataTable, { type Column } from '../../ui/DataTable';
import { StatusChip } from '../../ui/primitives';

interface Props {
  holdings: Holding[];
  showFund?: boolean;
  showHolder?: boolean;
  pageSize?: number;
}

export default function HoldingsTable({
  holdings,
  showFund = true,
  showHolder = true,
  pageSize = 25,
}: Props) {
  const scope = useTenantScope();
  const navigate = useNavigate();
  const total = sumBy(holdings, (holding) => holding.value);
  const hasCommitments = holdings.some((holding) => holding.commitment !== undefined);

  const columns: Column<Holding>[] = [
    ...(showHolder
      ? [
          {
            key: 'holder',
            label: 'Holder',
            sortValue: (row: Holding) => scope.holderById.get(row.holderId)?.name ?? '',
            render: (row: Holding) => {
              const holder = scope.holderById.get(row.holderId);
              return (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {holder?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {holder?.accountNumber} · {holder ? HOLDER_TYPE_LABELS[holder.type] : ''}
                  </Typography>
                </>
              );
            },
          },
        ]
      : []),
    ...(showFund
      ? [
          {
            key: 'fund',
            label: 'Fund / class',
            sortValue: (row: Holding) => scope.fundById.get(row.fundId)?.code ?? '',
            render: (row: Holding) => (
              <>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {scope.fundById.get(row.fundId)?.code}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {scope.classById.get(row.classId)?.name}
                </Typography>
              </>
            ),
          },
        ]
      : []),
    {
      key: 'units',
      label: 'Units',
      align: 'right',
      sortValue: (row) => row.units,
      render: (row) => formatUnits(row.units),
    },
    {
      key: 'value',
      label: 'Value',
      align: 'right',
      sortValue: (row) => row.value,
      render: (row) => money(row.value),
    },
    {
      key: 'pct',
      label: '% of class',
      align: 'right',
      hideBelow: 'md',
      sortValue: (row) => row.value,
      render: (row) => {
        const classTotal = sumBy(
          scope.holdings.filter((item) => item.classId === row.classId),
          (item) => item.units,
        );
        return classTotal ? `${((row.units / classTotal) * 100).toFixed(2)}%` : '—';
      },
    },
    ...(hasCommitments
      ? [
          {
            key: 'commitment',
            label: 'Commitment',
            align: 'right' as const,
            hideBelow: 'md' as const,
            sortValue: (row: Holding) => row.commitment ?? 0,
            render: (row: Holding) => (row.commitment ? money(row.commitment) : '—'),
          },
          {
            key: 'uncalled',
            label: 'Uncalled',
            align: 'right' as const,
            hideBelow: 'md' as const,
            sortValue: (row: Holding) => row.uncalled ?? 0,
            render: (row: Holding) => (row.uncalled ? money(row.uncalled) : '—'),
          },
        ]
      : []),
    {
      key: 'since',
      label: 'Holder since',
      hideBelow: 'lg',
      sortValue: (row) => row.firstDate,
      render: (row) => date(row.firstDate),
    },
    ...(showHolder
      ? [
          {
            key: 'flags',
            label: 'Flags',
            render: (row: Holding) => {
              const holder = scope.holderById.get(row.holderId);
              if (!holder) return null;
              return (
                <>
                  {holder.tfnStatus === 'NOT_QUOTED' && <StatusChip status="NOT_QUOTED" />}{' '}
                  {holder.kycStatus !== 'VERIFIED' && (
                    <StatusChip
                      status={holder.kycStatus}
                      label={`KYC ${holder.kycStatus.toLowerCase()}`}
                    />
                  )}{' '}
                  {holder.residency !== 'AU' && (
                    <StatusChip status="REPORTABLE" label={`Non-resident (${holder.residency})`} />
                  )}
                </>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <DataTable
      rows={holdings}
      columns={columns}
      rowKey={(row) => `${row.holderId}-${row.classId}`}
      onRowClick={
        showHolder ? (row) => navigate(`/manager/holders/sample/${row.holderId}`) : undefined
      }
      defaultSort={{ key: 'value', direction: 'desc' }}
      pageSize={pageSize}
      dense
      emptyMessage="No holdings match these filters"
      footer={
        holdings.length > 0 ? (
          <TableRow>
            <TableCell
              colSpan={(showHolder ? 1 : 0) + (showFund ? 1 : 0)}
              sx={{ fontWeight: 700, color: 'text.primary' }}
            >
              {holdings.length} holdings
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {formatUnits(sumBy(holdings, (holding) => holding.units))}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {money(total)}
            </TableCell>
            <TableCell colSpan={10} />
          </TableRow>
        ) : undefined
      }
    />
  );
}
